import { type IEvent } from '@solink/vd-data-layer/api-clients/platform/platform';
import { type HistogramRange } from '@solinkcorp/vd-common';
import omit from 'lodash/omit';
import moment from 'moment';
import {
  type BasicEventQueryParameters,
  generateQueryParameters,
  type GenerateQueryParametersParameters,
  getQueryRange,
} from 'utils/EventsFetchUtil';
import { HISTOGRAM_RANGES, WEB_QUERY_COUNT } from 'vd-constants';
import PlatformApi from './PlatformApi';

const MAX_RETRIES = 3;

// Sentinal value for a date before which there will never be any events.  We
// don't use 0 because it's falsey.  Could use `-Infinity`, but then
// `new Date(DAWN_OF_TIME)` would be an invalid date, and it's probably safe to
// assume there's never going to be an event before 1970.
export const DAWN_OF_TIME = 1;

export interface EventClientQueryParameters extends BasicEventQueryParameters {
  range?: HistogramRange | undefined;
}

type SortOrder = { [key: string]: { order: 'asc' | 'desc' } }[];

/**
 * Returns events centered around the given date.
 *
 * Events returned are in reverse chronological order, with the most recent event
 * at the 0th index.
 *
 * @returns events after (inclusive) the end date, in ascending order.
 */
export async function fetchEventsCenteredAround(
  parameters: EventClientQueryParameters,
  date: number | Date,
  isInFuture: boolean = false,
): Promise<{
  events: IEvent[];
  rangeStart: number;
  rangeEnd: number;
  earlierEventsAvailable: boolean;
  nextEarlier?: unknown[];
  nextMoreRecent?: unknown[];
}> {
  const { eventFilterState, range, selectedReportId } = parameters;

  let rangeStart: number;
  let rangeEnd: number;
  let earlierEventsAvailable: boolean = true;

  const queryDate = new Date(date);
  const queryDateMinusOneSecond = new Date(date.valueOf() - 1000);
  const queryCount = WEB_QUERY_COUNT / 2;
  const { value: timeDiff, unit: timeDiffUnit } = getQueryRange({
    range,
    selectedReportId,
    eventFilterState,
  });
  const start = subtractTimeWithRounding(date, timeDiff, timeDiffUnit);
  const end = addTimeWithRounding(date, timeDiff, timeDiffUnit);
  const queryEnd = getEndForMostRecent(end, isInFuture);

  const commonQuery = makeCommonQuery(parameters);

  const beforeParams = generateQueryParameters({
    ...commonQuery,
    count: queryCount,
    start: start.toISOString(),
    end: queryDateMinusOneSecond.toISOString(),
    sorts: [{ startTime: { order: 'desc' } }, { created: { order: 'desc' } }],
    source: '#fetchEventsCenteredAround-before',
  });
  const afterParams = generateQueryParameters({
    ...commonQuery,
    count: queryCount,
    start: queryDate.toISOString(),
    end: queryEnd.toISOString(),
    sorts: [{ startTime: { order: 'asc' } }, { created: { order: 'asc' } }],
    source: '#fetchEventsCenteredAround-after',
  });

  const [before, after] = await Promise.all([
    PlatformApi().listEvents(beforeParams),
    PlatformApi().listEvents(afterParams),
  ]);

  const beforeEvents = Array.isArray(before?.results) ? before.results : [];
  const afterEvents = Array.isArray(after?.results) ? after.results.reverse() : [];

  // The [startTime, created] cursors returned by Platform - used by useSolinkEvents
  // to paginate without re-deriving a timestamp cursor client-side.
  let nextEarlier: unknown[] | undefined = before?.next;
  const nextMoreRecent: unknown[] | undefined = after?.next;

  let events = afterEvents.concat(beforeEvents);

  if (beforeEvents.length > 0 && before.total > before.count) {
    rangeStart = beforeEvents[beforeEvents.length - 1].startTime.valueOf();
  } else {
    // There were no events between `start` and `queryDateMinusOneSecond`
    // or we got all existing events in this range back in one query,
    // so we have all of the events after `start`.
    rangeStart = start.valueOf();
  }

  if (afterEvents.length > 0 && after.total > after.count) {
    rangeEnd = afterEvents[0].startTime.valueOf();
  } else {
    // There were no events between `queryDate` and `end`
    // or we got all existing events in this range back in one query,
    // so we have all of the events before `end`.
    rangeEnd = end.valueOf();
  }

  if (events.length === 0) {
    const earlier = await fetchEarlierEvents(parameters, queryDate);
    events = earlier.events;
    rangeStart = earlier.rangeStart;
    earlierEventsAvailable = earlier.moreEventsToRequest;
    nextEarlier = earlier.next;
  }

  return {
    events,
    rangeStart,
    rangeEnd,
    earlierEventsAvailable,
    nextEarlier,
    nextMoreRecent,
  };
}

/**
 * Returns events earlier than or equal to the specified date.
 *
 * @param parameters - Filters for the events to fetch.
 * @param date - The date at or before to fetch events.
 * @param [options.startDate] - If specified, no events prior to this date will
 *   be fetched.
 * @param [options.next] - Platform [startTime, id] cursor to resume from. When
 *   present we page strictly forward from the cursor and skip the window-
 *   widening retry ladder - the cursor is unambiguous, so there's no risk of
 *   re-fetching events we already have.
 * @returns events from before (inclusive) the end date, in reverse chronological order.
 */
export async function fetchEarlierEvents(
  parameters: EventClientQueryParameters,
  date: Date | number,
  options: {
    startDate?: Date | number;
    next?: unknown[];
  } = {},
) {
  return fetchEarlierEventsHelper(parameters, date, options, MAX_RETRIES);
}

async function fetchEarlierEventsHelper(
  parameters: EventClientQueryParameters,
  date: Date | number,
  options: {
    startDate?: Date | number;
    next?: unknown[];
  } = {},
  retriesLeft: number,
): Promise<{
  events: IEvent[];
  moreEventsToRequest: boolean;
  rangeStart: number;
  rangeEnd: number;
  next?: unknown[];
}> {
  const { range, eventFilterState, selectedReportId } = parameters;

  const rangeEnd = date.valueOf();

  // Work out the start time based on the range.  The idea here is that we'll
  // try to find events from the past day, and if we can't find any we'll
  // retry and look for events from the past week, and then the past month,
  // and finally if that fails  we'll fall into the `else` case and look for
  // events from the dawn of time.
  //
  // We only bother with all this because, in the usual case where the events we
  // want are in the past day, specifying a date range narrows down what
  // Platform API needs to search for, and make the request much faster.
  let start: Date;
  if (retriesLeft !== 0) {
    const { value: timeDiff, unit: timeDiffUnit } = getQueryRange({
      range,
      selectedReportId,
      eventFilterState,
    });
    start = subtractTimeWithRounding(date, timeDiff, timeDiffUnit);
  } else {
    start = new Date(DAWN_OF_TIME);
  }

  if (options.startDate && options.startDate.valueOf() > start.valueOf()) {
    start = new Date(options.startDate.valueOf());
    retriesLeft = 0;
  }

  // Without a usable cursor, step back 1ms from the boundary so we don't
  // re-fetch the most recently loaded event. With a cursor, Platform handles
  // boundary exclusion via search_after and we use the raw end.
  const queryEnd = options.next ? date : date.valueOf() - 1;

  const { events, next: newNext } = await fetchEventsBetween(
    parameters,
    { start, end: queryEnd },
    {
      sortOrder: 'desc',
      count: WEB_QUERY_COUNT / 2,
      next: options.next,
    },
  );

  if (events.length > 0) {
    return {
      events,
      moreEventsToRequest: events.length >= WEB_QUERY_COUNT / 2,
      rangeStart: events[events.length - 1].startTime.valueOf(),
      rangeEnd,
      next: newNext,
    };
  }

  // No events in this window. If we have a cursor we've genuinely exhausted
  // the cursor's window - widening with a fresh fetch would re-read the data
  // already loaded by the caller, so just signal "no more". Otherwise, widen.
  if (options.next === undefined && retriesLeft > 0) {
    return fetchEarlierEventsHelper(
      { ...parameters, range: getNextRange(range || 'Day') },
      date,
      options,
      retriesLeft - 1,
    );
  }

  return {
    events: [],
    moreEventsToRequest: false,
    rangeStart: options.next === undefined ? DAWN_OF_TIME : start.valueOf(),
    rangeEnd,
    next: undefined,
  };
}

/**
 * Fetches event on or after the given date.
 *
 * Note that this returns events in ascending chronological order!
 *
 * @returns events after (inclusive) the end date, in chronological order.
 */
export async function fetchMoreRecentEvents(
  parameters: EventClientQueryParameters,
  date: Date | number,
  isInFuture: boolean = false,
  options: { next?: unknown[] } = {},
) {
  const { range, eventFilterState, selectedReportId } = parameters;

  const queryDate = new Date(date);
  const { value: timeDiff, unit: timeDiffUnit } = getQueryRange({
    range,
    selectedReportId,
    eventFilterState,
  });
  const end = addTimeWithRounding(date, timeDiff, timeDiffUnit);
  const queryEnd = getEndForMostRecent(end, isInFuture);

  return await fetchEventsBetween(
    parameters,
    { start: queryDate, end: queryEnd },
    {
      sortOrder: 'asc',
      count: WEB_QUERY_COUNT / 2,
      next: options.next,
    },
  );
}

/**
 * Fetches events between the specified start and end time.
 */
export async function fetchEventsBetween(
  parameters: Omit<EventClientQueryParameters, 'range'>,
  dateQuery: { start: number | Date; end: number | Date },
  options: {
    sortOrder?: 'asc' | 'desc' | SortOrder;
    count?: number;
    offset?: number;
    totalLimit?: boolean;
    next?: unknown[];
  },
) {
  const { start, end } = dateQuery;

  let sorts: { [key: string]: { order: 'asc' | 'desc' } }[] = [];

  if (options.sortOrder === 'asc' || options.sortOrder === 'desc') {
    // `created` tiebreaker so Platform's `next` cursor disambiguates events
    // sharing a startTime. `id` would be uniquer but isn't reliably indexed
    // across all subtypes (AI-generated bookmarks return null), whereas
    // `created` is the existing default in generateQueryParameters and is
    // indexed everywhere.
    sorts = [
      { startTime: { order: options.sortOrder } },
      { created: { order: options.sortOrder } },
    ];
  } else if (options.sortOrder) {
    sorts = options.sortOrder;
  } else {
    sorts = [{ startTime: { order: 'desc' } }, { created: { order: 'desc' } }];
  }

  const params = generateQueryParameters({
    ...makeCommonQuery(parameters),
    start: new Date(start).toISOString(),
    end: new Date(end).toISOString(),
    count: options.count ?? WEB_QUERY_COUNT,
    offset: options.offset,
    totalLimit: options.totalLimit,
    next: options.next,
    sorts,
    source: 'fetchEventsBetween',
  });

  const response = await PlatformApi().listEvents(params);
  const { results: events, total, next: newNext } = response;

  let rangeEnd: number;
  if (events.length === 0) {
    rangeEnd = end.valueOf();
  } else {
    rangeEnd = events[events.length - 1].startTime.valueOf();
  }

  return {
    events,
    total,
    next: newNext,
    rangeStart: start.valueOf(),
    rangeEnd,
  };
}

function subtractTimeWithRounding(date: Date | number, diff: number, unit: 'days' | 'hours') {
  return moment(date).subtract(diff, unit).startOf(unit).toDate();
}

function addTimeWithRounding(date: Date | number, diff: number, unit: 'days' | 'hours') {
  const now = Date.now();
  const addedDate = moment(date).add(diff, unit).endOf(unit);
  const newDate = moment(addedDate.valueOf() > now ? now : addedDate);
  return newDate.toDate();
}

function getNextRange(range: HistogramRange) {
  const ranges = HISTOGRAM_RANGES;
  const index = ranges.findIndex((element) => element === range);
  const nextIndex = index > -1 && index === ranges.length - 1 ? index : index + 1;
  return ranges[nextIndex] as HistogramRange;
}

function makeCommonQuery(
  parameters: EventClientQueryParameters,
): Omit<GenerateQueryParametersParameters, 'start' | 'end' | 'sortOrder' | 'source'> {
  return omit(parameters, 'range');
}

function getEndForMostRecent(end: Date, isInFuture: boolean = false) {
  let theEnd = end;
  if (isInFuture && end.getTime() >= Date.now() - 10000) {
    // If we need to fetch the scheduled activities that are in the future
    // and the end time past the current time,
    // increase the end time to a month later.
    // The scheduled activities are only created one hour before the scheduled time.
    theEnd = moment(end).add(1, 'month').toDate();
  }
  return theEnd;
}
